import { type CommandProps, type Editor, Extension, findParentNode, mergeAttributes, Node } from '@tiptap/core';
import { Image } from '@tiptap/extension-image';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { createColGroup, Table, TableKit, TableView } from '@tiptap/extension-table';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import type { Attrs, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { type EditorState, Plugin, PluginKey, type Selection, type Transaction } from '@tiptap/pm/state';
import { isInTable } from '@tiptap/pm/tables';
import type { EditorView } from '@tiptap/pm/view';
import { StarterKit } from '@tiptap/starter-kit';
import clamp from 'lodash/clamp';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';

import { TEXT_BLOCK_TYPES } from '@ui/components/editor/richTextUtils';
import { TemplateTag } from '@ui/components/editor/templateTagExtension';
import {
  NESTED_BLOCK_CONTAINER_SELECTOR,
  parseTemplateTableAlignment,
  readTemplateTableAlignment,
  renderTemplateTableAlignment,
  TABLE_ALIGN_DATA_ATTRIBUTE,
  TABLE_BODY_ELEMENT,
  TABLE_NODE_NAME,
  TEMPLATE_IMAGE_ATTRIBUTES,
  TEMPLATE_TEXT_ALIGNMENTS,
  TEMPLATE_TEXT_STARTER_KIT_OPTIONS,
  TEMPLATE_TEXT_STYLE_KIT_OPTIONS,
  TEMPLATE_TEXT_TABLE_KIT_OPTIONS,
  TEMPLATE_TEXT_TABLE_OPTIONS,
  toTemplateTableAlignment,
} from '@ui/components/editor/templateTextUtils';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    templateIndent: {
      indentTemplateBlocks: () => ReturnType;
      outdentTemplateBlocks: () => ReturnType;
    };
    tab: {
      insertTemplateTab: () => ReturnType;
    };
  }
}

const CONTENT_HOLE = 0;
const INSERTED_TEXT_TAG = 'ins';

export const TEMPLATE_MAX_INDENT_LEVEL = 8;
const TEMPLATE_INDENT_STEP_PIXELS = 40;
const TEMPLATE_INDENT_PRIORITY = 101;
const TEMPLATE_NESTED_INDENT_PLUGIN_KEY = new PluginKey('templateNestedIndent');
const NO_INDENT = 0;
const INDENT_STEP = 1;
const OUTDENT_STEP = -1;
const PIXEL_UNIT = 'px';
const INDENT_ATTRIBUTE = 'indent';
const FIRST_LINE_INDENT_ATTRIBUTE = 'firstLineIndent';
const INDENTABLE_BLOCK_TYPES = [...TEXT_BLOCK_TYPES, 'bulletList', 'orderedList'];
const LIST_ITEM_NODE = 'listItem';
const TOP_LEVEL_DEPTH = 1;
const TOP_LEVEL_LIST_ITEM_DEPTH = 2;
const BLOCK_START_OFFSET = 0;
const UNINDENTED_ATTRIBUTES = { [INDENT_ATTRIBUTE]: NO_INDENT, [FIRST_LINE_INDENT_ATTRIBUTE]: false };

const TEMPLATE_TAB_NODE_NAME = 'tab';
const TEMPLATE_TAB_ELEMENT = 'span';
const TEMPLATE_TAB_ATTRIBUTE = 'data-template-tab';
const TEMPLATE_TAB_SPACE_COUNT = 3;
const EM_SPACE = '\u2003';
const TEMPLATE_TAB_TEXT = EM_SPACE.repeat(TEMPLATE_TAB_SPACE_COUNT);
const TAB_CHARACTER = '\t';

interface TopLevelBlock {
  pos: number;
  node: ProseMirrorNode;
}

interface BlockAttributesChange {
  pos: number;
  attributes: Attrs;
}

class TemplateTableView extends TableView {
  constructor(node: ProseMirrorNode, cellMinWidth: number, view?: EditorView, HTMLAttributes?: Record<string, string>) {
    super(node, cellMinWidth, view, HTMLAttributes);
    const alignment = readTemplateTableAlignment(node);
    if (!isNil(alignment)) {
      this.table.setAttribute(TABLE_ALIGN_DATA_ATTRIBUTE, alignment);
    }
  }

  update(node: ProseMirrorNode): boolean {
    if (readTemplateTableAlignment(node) !== readTemplateTableAlignment(this.node)) {
      return false;
    }
    return super.update(node);
  }
}

const TemplateTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: parseTemplateTableAlignment,
        renderHTML: (attributes) => renderTemplateTableAlignment(toTemplateTableAlignment(attributes.align)),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const { colgroup, tableWidth, tableMinWidth } = createColGroup(node, this.options.cellMinWidth);
    const widthStyle = isEmpty(tableWidth) ? `min-width: ${tableMinWidth}` : `width: ${tableWidth}`;
    return [
      TABLE_NODE_NAME,
      mergeAttributes(this.options.HTMLAttributes, { style: widthStyle }, HTMLAttributes),
      colgroup,
      [TABLE_BODY_ELEMENT, CONTENT_HOLE],
    ];
  },
}).configure({ ...TEMPLATE_TEXT_TABLE_OPTIONS, View: TemplateTableView });

const readIndentLevel = (attributes: Attrs): number => attributes[INDENT_ATTRIBUTE] ?? NO_INDENT;

const hasFirstLineIndent = (attributes: Attrs): boolean => attributes[FIRST_LINE_INDENT_ATTRIBUTE] ?? false;

const hasIndent = (node: ProseMirrorNode): boolean =>
  readIndentLevel(node.attrs) > NO_INDENT || hasFirstLineIndent(node.attrs);

const isNestedBlockElement = (element: HTMLElement): boolean =>
  !isNil(element.parentElement?.closest(NESTED_BLOCK_CONTAINER_SELECTOR));

const readPositivePixels = (length: string): number => {
  const pixels = Number.parseFloat(length);
  return length.endsWith(PIXEL_UNIT) && pixels > NO_INDENT ? pixels : NO_INDENT;
};

const parseIndentLevel = (element: HTMLElement): number => {
  if (isNestedBlockElement(element)) {
    return NO_INDENT;
  }
  const level = Math.round(readPositivePixels(element.style.marginLeft) / TEMPLATE_INDENT_STEP_PIXELS);
  return clamp(level, NO_INDENT, TEMPLATE_MAX_INDENT_LEVEL);
};

const parseFirstLineIndent = (element: HTMLElement): boolean =>
  !isNestedBlockElement(element) && readPositivePixels(element.style.textIndent) > NO_INDENT;

const renderIndentLevel = (level: number): Record<string, string> =>
  level > NO_INDENT ? { style: `margin-left: ${level * TEMPLATE_INDENT_STEP_PIXELS}${PIXEL_UNIT}` } : {};

const renderFirstLineIndent = (indented: boolean): Record<string, string> =>
  indented ? { style: `text-indent: ${TEMPLATE_INDENT_STEP_PIXELS}${PIXEL_UNIT}` } : {};

const isIndentableBlock = (node: ProseMirrorNode): boolean => INDENTABLE_BLOCK_TYPES.includes(node.type.name);

const findTopLevelBlocks = ({ doc, selection }: EditorState): TopLevelBlock[] => {
  const blocks: TopLevelBlock[] = [];
  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    blocks.push({ pos, node });
    return false;
  });
  return blocks;
};

const readTopLevelBlock = ({ $from }: Selection): TopLevelBlock => ({
  pos: $from.before(TOP_LEVEL_DEPTH),
  node: $from.node(TOP_LEVEL_DEPTH),
});

const isInTopLevelIndentableTextblock = ({ $from }: Selection): boolean =>
  $from.depth === TOP_LEVEL_DEPTH && TEXT_BLOCK_TYPES.includes($from.parent.type.name);

const toIndentChange = ({ pos, node }: TopLevelBlock, step: number): BlockAttributesChange | null => {
  const level = readIndentLevel(node.attrs) + step;
  if (!isIndentableBlock(node) || level < NO_INDENT || level > TEMPLATE_MAX_INDENT_LEVEL) {
    return null;
  }
  return { pos, attributes: { ...node.attrs, [INDENT_ATTRIBUTE]: level } };
};

const toFirstLineIndentChange = ({ pos, node }: TopLevelBlock, indented: boolean): BlockAttributesChange | null =>
  hasFirstLineIndent(node.attrs) === indented
    ? null
    : { pos, attributes: { ...node.attrs, [FIRST_LINE_INDENT_ATTRIBUTE]: indented } };

const toOutdentChange = (block: TopLevelBlock): BlockAttributesChange | null =>
  readIndentLevel(block.node.attrs) > NO_INDENT
    ? toIndentChange(block, OUTDENT_STEP)
    : toFirstLineIndentChange(block, false);

const applyBlockChanges = ({ tr, dispatch }: CommandProps, changes: Array<BlockAttributesChange | null>): boolean => {
  const applicableChanges = compact(changes);
  if (isEmpty(applicableChanges)) {
    return false;
  }
  if (dispatch) {
    applicableChanges.forEach(({ pos, attributes }) => tr.setNodeMarkup(pos, undefined, attributes));
  }
  return true;
};

const changeBlock = (editor: Editor, change: BlockAttributesChange | null): boolean =>
  editor.commands.command((props) => applyBlockChanges(props, [change]));

const findListItem = (selection: Selection) => findParentNode((node) => node.type.name === LIST_ITEM_NODE)(selection);

const isInTopLevelList = (listItemDepth: number): boolean => listItemDepth === TOP_LEVEL_LIST_ITEM_DEPTH;

const deleteTemplateTabBeforeCaret = (editor: Editor): boolean =>
  editor.commands.command(({ tr, dispatch }) => {
    const { empty, $from } = tr.selection;
    const { nodeBefore } = $from;
    if (!empty || isNil(nodeBefore) || nodeBefore.type.name !== TEMPLATE_TAB_NODE_NAME) {
      return false;
    }
    if (dispatch) {
      tr.delete($from.pos - nodeBefore.nodeSize, $from.pos);
    }
    return true;
  });

const indentListItem = (editor: Editor, listItemDepth: number): boolean => {
  if (!editor.commands.sinkListItem(LIST_ITEM_NODE) && isInTopLevelList(listItemDepth)) {
    changeBlock(editor, toIndentChange(readTopLevelBlock(editor.state.selection), INDENT_STEP));
  }
  return true;
};

const outdentListItem = (editor: Editor, listItemDepth: number): boolean => {
  if (!isInTopLevelList(listItemDepth)) {
    return editor.commands.liftListItem(LIST_ITEM_NODE);
  }
  return changeBlock(editor, toIndentChange(readTopLevelBlock(editor.state.selection), OUTDENT_STEP));
};

const indentWithTab = (editor: Editor): boolean => {
  const { state } = editor;
  const { selection } = state;
  if (isInTable(state)) {
    return false;
  }
  if (findTopLevelBlocks(state).length > 1) {
    editor.commands.indentTemplateBlocks();
    return true;
  }
  const listItem = findListItem(selection);
  if (!isNil(listItem)) {
    return indentListItem(editor, listItem.depth);
  }
  const { $from, $to } = selection;
  if (!$from.parent.isTextblock) {
    return false;
  }
  if (!$from.sameParent($to)) {
    return true;
  }
  if (isInTopLevelIndentableTextblock(selection) && $from.parentOffset === BLOCK_START_OFFSET) {
    const block = readTopLevelBlock(selection);
    if (
      changeBlock(editor, toFirstLineIndentChange(block, true)) ||
      changeBlock(editor, toIndentChange(block, INDENT_STEP))
    ) {
      return true;
    }
  }
  return editor.commands.insertTemplateTab();
};

const outdentWithShiftTab = (editor: Editor): boolean => {
  const { state } = editor;
  const { selection } = state;
  if (isInTable(state)) {
    return false;
  }
  if (findTopLevelBlocks(state).length > 1) {
    return editor.commands.outdentTemplateBlocks();
  }
  const listItem = findListItem(selection);
  if (!isNil(listItem)) {
    return outdentListItem(editor, listItem.depth);
  }
  if (deleteTemplateTabBeforeCaret(editor)) {
    return true;
  }
  if (!isInTopLevelIndentableTextblock(selection)) {
    return false;
  }
  const block = readTopLevelBlock(selection);
  return (
    changeBlock(editor, toFirstLineIndentChange(block, false)) ||
    changeBlock(editor, toIndentChange(block, OUTDENT_STEP))
  );
};

const resetNestedIndents = (
  transactions: readonly Transaction[],
  _previousState: EditorState,
  state: EditorState
): Transaction | null => {
  if (!transactions.some((transaction) => transaction.docChanged)) {
    return null;
  }
  const { doc, tr } = state;
  doc.descendants((node, pos, parent) => {
    if (parent !== doc && hasIndent(node)) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...pick(UNINDENTED_ATTRIBUTES, Object.keys(node.attrs)) });
    }
    return !node.isTextblock;
  });
  return tr.docChanged ? tr : null;
};

const TemplateIndent = Extension.create({
  name: 'templateIndent',
  priority: TEMPLATE_INDENT_PRIORITY,

  addGlobalAttributes() {
    return [
      {
        types: INDENTABLE_BLOCK_TYPES,
        attributes: {
          [INDENT_ATTRIBUTE]: {
            default: NO_INDENT,
            parseHTML: parseIndentLevel,
            renderHTML: (attributes) => renderIndentLevel(readIndentLevel(attributes)),
          },
        },
      },
      {
        types: TEXT_BLOCK_TYPES,
        attributes: {
          [FIRST_LINE_INDENT_ATTRIBUTE]: {
            default: false,
            parseHTML: parseFirstLineIndent,
            renderHTML: (attributes) => renderFirstLineIndent(hasFirstLineIndent(attributes)),
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indentTemplateBlocks: () => (props) =>
        applyBlockChanges(
          props,
          findTopLevelBlocks(props.state).map((block) => toIndentChange(block, INDENT_STEP))
        ),
      outdentTemplateBlocks: () => (props) =>
        applyBlockChanges(props, findTopLevelBlocks(props.state).map(toOutdentChange)),
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => indentWithTab(this.editor),
      'Shift-Tab': () => outdentWithShiftTab(this.editor),
    };
  },

  addProseMirrorPlugins() {
    return [new Plugin({ key: TEMPLATE_NESTED_INDENT_PLUGIN_KEY, appendTransaction: resetNestedIndents })];
  },
});

const TemplateTab = Node.create({
  name: TEMPLATE_TAB_NODE_NAME,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,
  draggable: false,

  parseHTML() {
    return [{ tag: `${TEMPLATE_TAB_ELEMENT}[${TEMPLATE_TAB_ATTRIBUTE}]` }];
  },

  renderHTML() {
    return [TEMPLATE_TAB_ELEMENT, { [TEMPLATE_TAB_ATTRIBUTE]: '' }, TEMPLATE_TAB_TEXT];
  },

  renderText() {
    return TAB_CHARACTER;
  },

  addCommands() {
    return {
      insertTemplateTab:
        () =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.replaceSelectionWith(this.type.create(), true).scrollIntoView();
          }
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return { Backspace: () => deleteTemplateTabBeforeCaret(this.editor) };
  },
});

const TemplateUnderline = Underline.extend({
  parseHTML() {
    return [...(this.parent?.() ?? []), { tag: INSERTED_TEXT_TAG }];
  },
});

export const TEMPLATE_TEXT_EXTENSIONS = [
  StarterKit.configure(TEMPLATE_TEXT_STARTER_KIT_OPTIONS),
  TemplateUnderline,
  TemplateTable,
  TableKit.configure(TEMPLATE_TEXT_TABLE_KIT_OPTIONS),
  Image.configure({ inline: true, allowBase64: false, HTMLAttributes: TEMPLATE_IMAGE_ATTRIBUTES }),
  TextAlign.configure({ types: TEXT_BLOCK_TYPES, alignments: TEMPLATE_TEXT_ALIGNMENTS }),
  TextStyleKit.configure(TEMPLATE_TEXT_STYLE_KIT_OPTIONS),
  Subscript,
  Superscript,
  TemplateTag,
  TemplateIndent,
  TemplateTab,
];
